import { ChangeEvent, MouseEvent, ReactElement, forwardRef } from "react";

import {
  baseClass,
  checkedClass,
  defaultStateClass,
  disabledClass,
  inputClass,
  rootClass,
  wrapperClass,
} from "./card-network-radio-group-button.css";

export interface CardNetworkRadioGroupButtonProps {
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Type
   */
  type?: string;
  /**
   * Is the Button disabled?
   */
  isDisabled?: boolean;
  /**
   * Is button selected
   */
  isSelected: boolean;
  /**
   * onChange handler
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * onKeyDown handler
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * onBlur handler
   */
  onBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void;
  /**
   * onFocus handler
   */
  onFocus?: (e: React.FocusEvent<HTMLDivElement, Element>) => void;
  /**
   * Label to display with radio button
   */
  title?: ReactElement | string;
  /**
   * Title component
   */
  tabIndex?: number;
  /**
   * Value of radio button
   */
  value: string;
}

export const CardNetworkRadioGroupButton = forwardRef<
  HTMLInputElement,
  CardNetworkRadioGroupButtonProps
>(
  (
    {
      id,
      isDisabled,
      isSelected,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      tabIndex,
      title,
      value,
    }: CardNetworkRadioGroupButtonProps,
    ref
  ) => {
    const selectedClassLogic: string = isSelected ? checkedClass : "";
    const disabledClassLogic: string = isDisabled ? disabledClass : "";

    const onClickSection = (e: MouseEvent) => {
      e.stopPropagation();
      const event = {
        ...e,
        target: {
          ...e.target,
          value: value,
        },
      } as unknown as ChangeEvent<HTMLInputElement>;
      onChange(event);
    };

    return (
      <div
        role="radio"
        data-testid={id}
        id={id}
        aria-label={`${id}-radioButton`}
        aria-checked={isSelected}
        tabIndex={isSelected ? tabIndex : -1}
        className={`${rootClass}`}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClickSection}
      >
        <div
          className={`${wrapperClass}`}
          data-testid={`${id}-radioButtonWrapper`}
        >
          <input
            className={`${inputClass}`}
            checked={isSelected}
            disabled={isDisabled}
            id={`${id}-input`}
            type="radio"
            name={`${id}-input`}
            ref={ref}
            value={value}
          />
          <span
            className={`${baseClass} ${defaultStateClass} ${selectedClassLogic} ${disabledClassLogic}`}
          />
        </div>
        {title}
      </div>
    );
  }
);

CardNetworkRadioGroupButton.displayName = "RadioButton";
